/**
 * 2 — Design fit & aspect: how the design fills the print area, plus the test design.
 *
 * Extracted from `MaskEditor.tsx` (WS-J J-12). Fill mode, aspect, rotation, the live preview button
 * and the testing-only stand-in design — unchanged, testids included.
 */
import type { FitMode } from "@genz/engine-bind";
import { Icon } from "../../ui/Icon.js";
import { FILL_FIT_LABEL } from "../controlLabels.js";
import type { ComposeReason } from "../maskEditorTypes.js";

export interface DesignFitCardProps {
  locked?: boolean;
  chromaReady: boolean;
  fillFit: FitMode;
  setFillFit: (v: FitMode) => void;
  designAspect: [number, number] | null;
  setDesignAspect: (v: [number, number] | null) => void;
  designRotation: number;
  setDesignRotation: React.Dispatch<React.SetStateAction<number>>;
  testDesign: Uint8Array | null;
  testDesignName: string;
  testDesignInputRef: React.RefObject<HTMLInputElement | null>;
  onTestDesignFile: (file: File | null | undefined) => void;
  clearTestDesignNow: () => void;
  previewMockup: (uploadBake?: boolean, reason?: ComposeReason) => Promise<boolean>;
  setRemovalVer: React.Dispatch<React.SetStateAction<number>>;
  /** Whether an uploaded test design is kept with the template on this device (the editor's
   *  `templateId` is set). The wizard has no template yet, so its test design is a preview only
   *  — WS-AUTOKEY says so instead of promising a save that never happens. */
  testDesignSaved?: boolean;
}

export function DesignFitCard(p: DesignFitCardProps) {
  const {
    locked, chromaReady, fillFit, setFillFit, designAspect,
    setDesignAspect, designRotation, setDesignRotation, testDesign, testDesignName,
    testDesignInputRef, onTestDesignFile, clearTestDesignNow, previewMockup, setRemovalVer,
  } = p;
  const saved = p.testDesignSaved !== false;
  return (
    <>
        {/* 2 — Design fit & aspect: how the design fills the print area (fill mode + aspect +
            rotation), the live preview, and a testing-only stand-in design. */}
        {/* WS-Z6 / R12: the card is an accordion BODY now — the head, its marker and the
            open key belong to the rail. `data-testid` and every control are unchanged. */}
        <div className="mask-editor__section-body" data-testid="section-design-fit">
          <div className="mask-editor__chroma-row">
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => void previewMockup()}
              disabled={locked || !chromaReady}
              title="Composite the layered mockup via the engine (test design in the print area + chosen background)"
              data-testid="preview-mockup"
            >
              <Icon name="image" size={16} /> Preview mockup
            </button>
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => { setDesignRotation((r) => (r + 90) % 360); }}
              disabled={locked}
              title="Rotate the design placement 90° (fills the print-area height, keeps aspect). Saved with the template."
              data-testid="rotate-placement"
            >
              <Icon name="refresh" size={16} /> Rotate 90°{designRotation ? ` · ${designRotation}°` : ""}
            </button>
            <label className="mask-editor__fill faint mask-editor__inline-field" aria-label="Design fill mode">
              Fit
              <select
                className="input mask-editor__select--md"
                data-testid="fill-mode"
                disabled={locked}
                value={fillFit}
                onChange={(e) => { setFillFit(e.target.value as FitMode); setRemovalVer((v) => v + 1); }}
              >
                {/* WS-Z6: one label per value (`editor/controlLabels.ts`) — the rail's
                    accordion head prints the same string. */}
                {(Object.entries(FILL_FIT_LABEL) as [FitMode, string][]).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
            <label className="mask-editor__aspect faint mask-editor__inline-field" aria-label="Design aspect ratio">
              Aspect
              <select
                className="input mask-editor__select--sm"
                data-testid="aspect-mode"
                disabled={locked}
                value={designAspect ? `${designAspect[0]}x${designAspect[1]}` : "original"}
                onChange={(e) => {
                  const v = e.target.value;
                  setDesignAspect(v === "original" ? null : (v.split("x").map(Number) as [number, number]));
                  setRemovalVer((rv) => rv + 1);
                }}
              >
                <option value="original">Original</option>
                <option value="1x1">1:1</option>
                <option value="4x5">4:5</option>
                <option value="3x4">3:4</option>
                <option value="16x9">16:9</option>
                <option value="9x16">9:16</option>
              </select>
            </label>
          </div>
          {/* TEST DESIGN (testing-only) — drop a stand-in artwork; it fills the print area in the
              live mockup. The print area itself is keyed AUTOMATICALLY from the magenta marker
              (no manual keying). Saved with the template so it's there next session. */}
          <div className="row mask-editor__td-row" data-testid="test-design-row">
            <input
              ref={testDesignInputRef}
              type="file"
              accept="image/*"
              className="is-hidden"
              onChange={(e) => onTestDesignFile(e.target.files?.[0])}
              data-testid="test-design-input"
            />
            <button
              type="button"
              className="btn btn--sm"
              disabled={locked}
              onClick={() => testDesignInputRef.current?.click()}
              data-testid="test-design-upload"
              title={saved
                ? "Upload a stand-in design to preview live in the print area — saved with the template (testing only)"
                : "Upload a design to see the mock-up — a preview only, not saved with the template"}
            >
              <Icon name="brush" size={16} /> {testDesign ? "Change test design" : "Upload test design"}
            </button>
            {testDesign && (
              <>
                <span className="faint mask-editor__td-name" data-testid="test-design-name">
                  {testDesignName || "design"}
                </span>
                <button type="button" className="btn btn--sm btn--ghost" disabled={locked} onClick={clearTestDesignNow} data-testid="test-design-clear">
                  Remove
                </button>
              </>
            )}
          </div>
          {saved ? (
            <span className="mask-editor__hint mask-editor__hint--quiet" data-testid="test-design-hint"
              title="Print area is keyed automatically from the print guide — your test design fills it. Pick a background below, then “Preview mockup”.">
              Marker keys the print area automatically — pick a background, then Preview mockup.
            </span>
          ) : (
            <span className="mask-editor__hint mask-editor__hint--quiet" data-testid="test-design-hint">
              Upload a test design, then Preview mockup. The test design is not saved.
            </span>
          )}
        </div>
    </>
  );
}
